import PropTypes from 'prop-types';
import React from 'react';
import { FormattedMessage, FormattedNumber } from 'react-intl';
import connectToStores from 'fluxible-addons-react/connectToStores';

import { Icon } from '@99designs/common/components/Icon';

import StickyElement from 'common/components/StickyElement';

import OverviewPanel from 'book-calculator/components/OverviewPanel';
import OverviewMenu from 'book-calculator/components/OverviewMenu';
import OverviewMenuItem from 'book-calculator/components/OverviewMenuItem';
import BookCalculatorAmountStore from 'book-calculator/stores/BookCalculatorAmountStore';

const Overview = ({
    totalAmount,
    editingAmount,
    coverAmount,
    interiorAmount,
    printingAmount,
    extrasAmount,
    isUpdating,
    seenSteps,
    canRenderPrinting,
}) => (
    <StickyElement offsetTop={30} variations={['overview-panel']}>
        <OverviewPanel summaryAmount={renderAmount(totalAmount)}>
            <OverviewMenu
                heading={<FormattedMessage id="overview.heading" />}
                summaryAmount={renderAmount(totalAmount)}
                isUpdating={isUpdating}
            >
                <OverviewMenuItem
                    icon={<Icon type={'tick-2'} />}
                    value={renderAmount(editingAmount)}
                    text={<FormattedMessage id="steps.editing.heading" />}
                    href="#book-calculator-editing-step"
                    isUpdating={isUpdating}
                    isInactive={!seenSteps.editingStep}
                />
                <OverviewMenuItem
                    icon={<Icon type={'tick-2'} />}
                    value={renderAmount(coverAmount)}
                    text={<FormattedMessage id="steps.cover.heading" />}
                    href="#book-calculator-cover-step"
                    isUpdating={isUpdating}
                    isInactive={!seenSteps.coverStep}
                />
                <OverviewMenuItem
                    icon={<Icon type={'tick-2'} />}
                    value={renderAmount(interiorAmount)}
                    text={<FormattedMessage id="steps.interior.heading" />}
                    href="#book-calculator-interior-step"
                    isUpdating={isUpdating}
                    isInactive={!seenSteps.interiorStep}
                />

                {canRenderPrinting
                    ? renderPrintingItem({
                          value: renderAmount(printingAmount),
                          isUpdating,
                          isInactive: !seenSteps.printingStep,
                      })
                    : null}

                <OverviewMenuItem
                    icon={<Icon type={'tick-2'} />}
                    value={renderAmount(extrasAmount)}
                    text={<FormattedMessage id="steps.extras.heading" />}
                    href="#book-calculator-extras-step"
                    isUpdating={isUpdating}
                    isInactive={!seenSteps.extrasStep}
                />
            </OverviewMenu>
        </OverviewPanel>
    </StickyElement>
);

Overview.propTypes = {
    totalAmount: PropTypes.number.isRequired,
    editingAmount: PropTypes.number.isRequired,
    coverAmount: PropTypes.number.isRequired,
    interiorAmount: PropTypes.number.isRequired,
    printingAmount: PropTypes.number.isRequired,
    extrasAmount: PropTypes.number.isRequired,
    isUpdating: PropTypes.bool.isRequired,
    canRenderPrinting: PropTypes.bool,
    seenSteps: PropTypes.object,
};

Overview.defaultProps = {
    seenSteps: {},
    canRenderPrinting: true,
};

function renderAmount(value) {
    if (value) {
        return (
            <FormattedNumber
                value={value}
                style="currency"
                currency={'USD'}
                minimumFractionDigits={0}
                maximumFractionDigits={0}
            />
        );
    }

    return null;
}

function renderPrintingItem(attrs) {
    return (
        <OverviewMenuItem
            {...attrs}
            icon={<Icon type={'tick-2'} />}
            text={<FormattedMessage id="steps.printing.heading" />}
            href="#book-calculator-printing-step"
        />
    );
}

export default connectToStores(Overview, [BookCalculatorAmountStore], (context, prop) => {
    const amountStore = context.getStore(BookCalculatorAmountStore);
    const amounts = amountStore.getAmounts();
    const isLoading = amountStore.isLoading();

    return {
        totalAmount: amounts.total,
        editingAmount: amounts.editing,
        coverAmount: amounts.cover,
        interiorAmount: amounts.interior,
        printingAmount: amounts.printing,
        extrasAmount: amounts.extras,
        isUpdating: isLoading,
    };
});
